// // src/pages/Trend/components/analysis/consumer-reaction/AspectChart.js
// import React, { memo } from 'react';
// import { ErrorMessage } from '../../../../../components/common/UI';
// import { ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip, Legend } from 'recharts';
// import './AspectChart.css';
// const processChartData = (data) => {
//   if (!data?.aspect_scores) return [];

//   const { moisture, effectiveness, texture, absorption } = data.aspect_scores;

//   return [
//     { aspect: "보습력", score: moisture },
//     { aspect: "효과", score: effectiveness },
//     { aspect: "제형", score: texture },
//     { aspect: "흡수력", score: absorption }
//   ];
// };

// const AspectChart = ({ data }) => {
//   if (!data?.primary?.aspect_scores) {
//     return <ErrorMessage message="속성 분석 데이터를 불러올 수 없습니다." />;
//   }

//   const { primary, secondary } = data;
//   const hasSecondaryData = secondary?.aspect_scores !== undefined;

//   const primaryData = processChartData(primary);
//   const secondaryData = hasSecondaryData ? processChartData(secondary) : null;

//   return (
//     <div className="aspect-chart-container">
//       <div className="chart-wrapper">
//         <ResponsiveContainer width="100%" height="100%">
//           <RadarChart cx="50%" cy="50%" outerRadius="70%" data={primaryData}>
//             <PolarGrid gridType="polygon" />
//             <PolarAngleAxis 
//               dataKey="aspect" 
//               tick={{ 
//                 fill: '#666', 
//                 fontSize: 14,
//                 dy: -2,
//                 dx: 2
//               }} 
//             />
//             <PolarRadiusAxis 
//               angle={90} 
//               domain={[0, 100]} 
//               tick={{ fontSize: 12 }} 
//             />
//             <Tooltip 
//               formatter={(value) => `${value.toFixed(1)}%`} 
//               labelStyle={{ color: '#666' }} 
//             />
//             {hasSecondaryData && <Legend />}
//             <Radar 
//               name={hasSecondaryData ? "필터 1" : ""} 
//               dataKey="score" 
//               stroke="#4EBBFF"
//               fill="#4EBBFF" 
//               fillOpacity={0.8} 
//             />
//             {hasSecondaryData && (
//               <Radar 
//                 name="필터 2" 
//                 dataKey="score" 
//                 stroke="rgb(255, 132, 143)" 
//                 fill="rgb(255, 132, 143)" 
//                 fillOpacity={0.6} 
//                 data={secondaryData}
//               />
//             )}
//           </RadarChart>
//         </ResponsiveContainer>
//       </div>
//     </div>
//   );
// };

// export default memo(AspectChart); 

// src/pages/Trend/components/analysis/consumer-reaction/AspectChart.js
import React, { memo, useMemo } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { ErrorMessage } from '../../../../../components/common/UI';
import { ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip, Legend } from 'recharts';
import './AspectChart.css';

// BBC Style Configuration
const BBC_STYLE = {
  FONT_FAMILY: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  PRIMARY_COLOR: '#1380A1',
  SECONDARY_COLOR: '#FAAB18',
  HIGHLIGHT_COLOR: '#D55E00',
  TEXT_COLOR: '#333333',
  LIGHT_TEXT: '#666666',
  AXIS_COLOR: '#B0B0B0',
  BORDER_COLOR: '#E5E7EB'
};

// Render filter label with monospace number for alignment
const renderFilterLabel = (label) => {
  const match = label.match(/^(\d+)(.*)$/);
  if (match) {
    return (
      <>
        <span className="filter-label-number">{match[1]}</span>
        {match[2]}
      </>
    );
  }
  return label;
};

// Custom Tooltip matching SentimentChart style
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div style={{
      background: 'white',
      borderRadius: '8px',
      padding: '12px 16px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
      minWidth: '140px'
    }}>
      <div style={{
        fontSize: '11px',
        fontWeight: '600',
        color: '#999999',
        marginBottom: '10px'
      }}>
        {label}
      </div>
      {payload.map((entry, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '3px 0',
            gap: '16px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: entry.color
            }} />
            <span style={{ fontSize: '11px', color: '#666666' }}>
              {entry.name || 'Score'}
            </span>
          </div>
          <span style={{
            fontSize: '11px',
            fontWeight: '500',
            fontFamily: BBC_STYLE.FONT_FAMILY,
            color: BBC_STYLE.TEXT_COLOR
          }}>
            {entry.value?.toFixed(1)}%
          </span>
        </div>
      ))}
    </div>
  );
};

const AspectChart = ({ data }) => {
  const { translate } = useLanguage();

  const processChartData = (sourceData) => {
    if (!sourceData?.aspect_scores) return [];

    const { moisture, effectiveness, texture, absorption } = sourceData.aspect_scores;

    return [
      { aspect: translate('trendPage.consumerReaction.aspects.moisture'), score: moisture, key: 'moisture' },
      { aspect: translate('trendPage.consumerReaction.aspects.effectiveness'), score: effectiveness, key: 'effectiveness' },
      { aspect: translate('trendPage.consumerReaction.aspects.texture'), score: texture, key: 'texture' },
      { aspect: translate('trendPage.consumerReaction.aspects.absorption'), score: absorption, key: 'absorption' }
    ];
  };

  // Find highest scoring aspect
  const getHighestAspect = (chartData) => {
    if (!chartData || chartData.length === 0) return null;
    return chartData.reduce((max, item) => item.score > max.score ? item : max, chartData[0]);
  };

  const { primary, secondary } = data || {};
  const hasSecondaryData = secondary?.aspect_scores !== undefined;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const primaryData = useMemo(() => processChartData(primary), [primary, translate]);
  const secondaryData = useMemo(
    () => hasSecondaryData ? processChartData(secondary) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasSecondaryData, secondary, translate]
  );

  const highestPrimary = useMemo(() => getHighestAspect(primaryData), [primaryData]);
  const highestSecondary = useMemo(() =>
    hasSecondaryData ? getHighestAspect(secondaryData) : null,
    [hasSecondaryData, secondaryData]
  );

  if (!data?.primary?.aspect_scores) {
    return <ErrorMessage message={translate('trendPage.consumerReaction.errors.noAspectData')} />;
  }

  return (
    <div className="aspect-chart-container" style={{ fontFamily: BBC_STYLE.FONT_FAMILY }}>
      {/* Narrative Section */}
      <div className="aspect-narrative">
        {!hasSecondaryData ? (
          // Single mode narrative
          <h2 style={{
            fontSize: '1.1rem',
            fontWeight: '600',
            color: BBC_STYLE.TEXT_COLOR,
            margin: '0 0 8px 0',
            lineHeight: '1.4'
          }}>
            <span style={{
              fontWeight: '700',
              color: BBC_STYLE.HIGHLIGHT_COLOR,
              borderBottom: `2px solid ${BBC_STYLE.HIGHLIGHT_COLOR}`,
              paddingBottom: '1px'
            }}>
              {highestPrimary?.aspect}
            </span>
            {' '}{translate('trendPage.consumerReaction.aspectNarrative.title')}{' '}
            <span style={{ fontWeight: '700' }}>
              {highestPrimary?.score?.toFixed(1)}%
            </span>
          </h2>
        ) : (
          // Comparison mode narrative
          <h2 style={{
            fontSize: '1.1rem',
            fontWeight: '600',
            color: BBC_STYLE.TEXT_COLOR,
            margin: '0 0 8px 0',
            lineHeight: '1.4'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: BBC_STYLE.PRIMARY_COLOR
              }} />
              <span style={{ color: BBC_STYLE.LIGHT_TEXT }}>{renderFilterLabel(translate('trendPage.filters.set1'))}:</span>
              <span style={{ fontWeight: '700', color: BBC_STYLE.PRIMARY_COLOR }}>
                {highestPrimary?.aspect}
              </span>
              <span>{highestPrimary?.score?.toFixed(1)}%</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: BBC_STYLE.SECONDARY_COLOR
              }} />
              <span style={{ color: BBC_STYLE.LIGHT_TEXT }}>{renderFilterLabel(translate('trendPage.filters.set2'))}:</span>
              <span style={{ fontWeight: '700', color: BBC_STYLE.SECONDARY_COLOR }}>
                {highestSecondary?.aspect}
              </span>
              <span>{highestSecondary?.score?.toFixed(1)}%</span>
            </div>
          </h2>
        )}
        <div style={{
          fontSize: '0.85rem',
          color: BBC_STYLE.LIGHT_TEXT,
          margin: '0 0 20px 0',
          lineHeight: '1.5'
        }}>
          {translate('trendPage.consumerReaction.aspectNarrative.subtitle')}
        </div>
      </div>

      {/* Chart */}
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={380}>
          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={primaryData}>
            <PolarGrid
              gridType="polygon"
              stroke={BBC_STYLE.AXIS_COLOR}
            />
            <PolarAngleAxis
              dataKey="aspect"
              tick={{
                fill: BBC_STYLE.TEXT_COLOR,
                fontSize: 14,
                dy: -2,
                dx: 2
              }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={{
                fontSize: 12,
                fill: BBC_STYLE.AXIS_COLOR
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            {hasSecondaryData && (
              <Legend
                wrapperStyle={{
                  paddingTop: '20px'
                }}
              />
            )}
            <Radar
              name={hasSecondaryData ? translate('trendPage.filters.set1') : ""}
              dataKey="score"
              stroke={BBC_STYLE.PRIMARY_COLOR}
              fill={BBC_STYLE.PRIMARY_COLOR}
              fillOpacity={0.6}
            />
            {hasSecondaryData && (
              <Radar
                name={translate('trendPage.filters.set2')}
                dataKey="score"
                stroke={BBC_STYLE.SECONDARY_COLOR}
                fill={BBC_STYLE.SECONDARY_COLOR}
                fillOpacity={0.4}
                data={secondaryData}
              />
            )}
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default memo(AspectChart);
